import { api } from '@/api'
import type {
  AccessPreviewResponse,
  AllowedScopesRequestBody,
  AllowedScopesResponse,
  AssignRoleRequestBody,
  RoleAssignmentResponse,
} from '@/api/access.types'
import type { AccessPreview, RoleAssignment } from '@/features/users'
import type { AllowedScope } from '../types'
import { toAccessPreview, toAllowedScopes, toRoleAssignment } from '../transform/accessTransformer'

// The IDP's subject-keyed access management for a service account, reached
// through the BFF proxy. A service account is addressed by its clientId UUID
// in every path here — the server resolves it to the SA's email (the actual
// role_assignments/allowed_scopes subject key) internally.
const BASE = '/v1/admin/serviceaccounts'

// GET /v1/admin/serviceaccounts/{id}/role-assignments → every (product,
// roleId) the service account holds.
export function listServiceAccountRoleAssignments(id: string): Promise<RoleAssignment[]> {
  return api
    .get<RoleAssignmentResponse[]>(`${BASE}/${id}/role-assignments`)
    .then((assignments) => assignments.map(toRoleAssignment))
}

// POST /v1/admin/serviceaccounts/{id}/role-assignments.
export function assignServiceAccountRole(id: string, product: string, roleId: string): Promise<void> {
  const body: AssignRoleRequestBody = { product, roleId }
  return api.post(`${BASE}/${id}/role-assignments`, { body }).then(() => undefined)
}

// DELETE /v1/admin/serviceaccounts/{id}/role-assignments/{product}/{roleId}.
export function revokeServiceAccountRole(id: string, product: string, roleId: string): Promise<void> {
  return api.delete(`${BASE}/${id}/role-assignments/${product}/${roleId}`).then(() => undefined)
}

// GET /v1/admin/serviceaccounts/{id}/access?product= → the service account's
// full resolved entitlement for the product, uncapped by its own allowed-
// scopes ceiling (there is no minting client in an admin preview).
export function getServiceAccountAccessPreview(id: string, product: string): Promise<AccessPreview> {
  return api
    .get<AccessPreviewResponse>(`${BASE}/${id}/access`, { params: { product } })
    .then(toAccessPreview)
}

// GET /v1/admin/serviceaccounts/{id}/allowed-scopes → the current mint
// ceiling, empty when uncapped.
export function getServiceAccountAllowedScopes(id: string): Promise<AllowedScope[]> {
  return api.get<AllowedScopesResponse>(`${BASE}/${id}/allowed-scopes`).then(toAllowedScopes)
}

// PUT /v1/admin/serviceaccounts/{id}/allowed-scopes — full replace.
export function putServiceAccountAllowedScopes(id: string, scopeIds: string[]): Promise<AllowedScope[]> {
  const body: AllowedScopesRequestBody = { scopeIds }
  return api.put<AllowedScopesResponse>(`${BASE}/${id}/allowed-scopes`, { body }).then(toAllowedScopes)
}
